import type { ReactNode } from 'react'
import { ApiContractError, ApiError } from '@/api/client'
import { BarList } from '@/components/charts/BarList'
import { ChartCard } from '@/components/charts/ChartCard'
import { LineChart } from '@/components/charts/LineChart'
import { StackedShare } from '@/components/charts/StackedShare'
import { StatTile } from '@/components/charts/StatTile'
import { useProducttypeStatistics } from '@/features/topics/useTopics'
import {
  distributionByCategory,
  distributionByMarginGroup,
  seasonality,
  topTopicsByRanking,
  topTopicsByVolume,
} from '@/features/stats/statsFromTopics'
import { formatNumber } from '@/lib/format'

export function StatsPage(): ReactNode {
  /*
   * Alle Status, denn eine Statistik, die freigegebene und ignorierte
   * Themen ausblendet, beantwortet die Frage "wie weit sind wir" gerade
   * nicht. Die Aggregation läuft serverseitig, deshalb keine Seitengrenze
   * mehr wie beim früheren Vollabruf.
   */
  const { data, isLoading, error } = useProducttypeStatistics({
    status: ['Open', 'Approved', 'Ignored'],
  })

  const season = data ? seasonality(data) : []

  if (error) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="rounded border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            {error instanceof ApiError || error instanceof ApiContractError
              ? error.message
              : 'Die Auswertung konnte nicht geladen werden.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Überblick</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Auswertung über {formatNumber(data?.totalCount ?? 0)} Themen.
        </p>
        {data?.isComplete === false && (
          <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
            Die Berechnung ist noch nicht vollständig abgeschlossen. Die Zahlen unten können sich
            noch ändern.
          </p>
        )}
      </header>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-3">
        <StatTile
          label="Themen gesamt"
          value={formatNumber(data?.totalCount ?? 0)}
          isLoading={isLoading}
        />
        <StatTile
          label="Noch offen"
          value={formatNumber(data?.statusCounts.open ?? 0)}
          context="warten auf Sichtung"
          isLoading={isLoading}
        />
        <StatTile
          label="Freigegeben"
          value={formatNumber(data?.statusCounts.approved ?? 0)}
          context="als Nische angelegt"
          isLoading={isLoading}
        />
        <StatTile
          label="Bekanntes Potenzial"
          value={formatNumber(data?.knownPotentialSum ?? 0)}
          context="Suchanfragen pro Monat"
          isLoading={isLoading}
        />
        <StatTile
          label="Ignoriert"
          value={formatNumber(data?.statusCounts.ignored ?? 0)}
          context="aussortiert"
          isLoading={isLoading}
        />
        <StatTile
          label="Im Ranking"
          value={formatNumber(data?.rankedCount ?? 0)}
          context="mit gerechnetem Score"
          isLoading={isLoading}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard
          title="Bearbeitungsstand"
          description="Wie viele Themen bereits entschieden sind."
        >
          <StackedShare
            segments={[
              { label: 'Offen', value: data?.statusCounts.open ?? 0, color: 'var(--viz-series-1)' },
              {
                label: 'Freigegeben',
                value: data?.statusCounts.approved ?? 0,
                color: 'var(--viz-series-3)',
              },
              {
                label: 'Ignoriert',
                value: data?.statusCounts.ignored ?? 0,
                color: 'var(--viz-series-2)',
              },
            ]}
          />
        </ChartCard>

        <ChartCard title="Themen je Kategorie" description="Verteilung über die Taxonomie.">
          <BarList data={data ? distributionByCategory(data) : []} />
        </ChartCard>

        <ChartCard
          title="Stärkste Themen"
          description="Nach dem größten Einzel-Suchvolumen im Thema."
        >
          <BarList data={data ? topTopicsByVolume(data) : []} />
        </ChartCard>

        {/*
         * Score und Suchvolumen bekommen bewusst zwei getrennte Karten.
         * Der Score verrechnet Volumen und Marge, beides in eine Grafik
         * zu legen bräuchte zwei Y-Achsen und wäre nicht mehr lesbar.
         */}
        <ChartCard
          title="Bestes Ranking"
          description="Nach dem Score aus Suchvolumen und Marge. Themen ohne gerechnetes Ranking fehlen."
        >
          <BarList
            data={data ? topTopicsByRanking(data) : []}
            emptyMessage="Noch kein Ranking gerechnet"
          />
        </ChartCard>

        <ChartCard
          title="Themen je Marge-Gruppe"
          description="Wo die Nachfrage auf welche Marge trifft."
        >
          <BarList data={data ? distributionByMarginGroup(data) : []} />
        </ChartCard>

        <ChartCard
          title="Saisonverlauf"
          description="Suchvolumen aller Themen je Monat."
          className="lg:col-span-2"
        >
          {season.length > 0 ? (
            <LineChart data={season} seriesName="Suchvolumen gesamt" />
          ) : (
            <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              Noch keine Monatswerte vorhanden. Sie entstehen, sobald die Keyword-Discovery gelaufen
              ist.
            </p>
          )}
        </ChartCard>
      </div>
    </div>
  )
}
